import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Filter from './Filter';
import { IColInfo, IColumnUserSettings, ITableDataProps } from './Interfaces';
import TableBody from './TableBody';
import { TableDataProcessor } from './TableDataProcessor';
import TableFooter from './TableFooter';
import TableHeader from './TableHeader';
import ToggleColumn from './ToggleColumn';
import DataExporter from './DataExporter';


const DataTable: React.FC<ITableDataProps> = ({ name, colInfos, tableData, pageSize, serverErrors, refreshNr, navigateTitle, navigateUrl, onCellChange, onRowClick, maxHeight, maxWidth, resizable = true, showToggleColumn = true, onSelectedRowsChange }) => {
    const [data, setData] = useState<any[] | undefined>()
    const [totalFilteredRows, setTotalFilteredRows] = useState(0)
    const [dataProcessor] = useState<TableDataProcessor>(new TableDataProcessor())
    const [cols, setCols] = useState<IColInfo[]>(colInfos)
    const [colUserSettings, setColUserSettings] = useState<IColumnUserSettings[]>([])
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [currentPageSize, setPageSize] = useState<number>(pageSize ?? 1000)
    const [startRow, setStartRow] = useState<number>(0)
    const [endRow, setEndRow] = useState<number>(999)
    const navigate = useNavigate()

    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [filterColInfo, setFilterColInfo] = useState<IColInfo>(cols[0]);
    const [filterColIndex, setFilterColIndex] = useState<number>(0);
    const [filterMenuTop, setFilterMenuTop] = useState<number>(0);
    const [filterMenuLeft, setFilterMenuLeft] = useState<number>(0);
    const [filterMenuHeight, setFilterMenuHeight] = useState<number>(0);

    const [hasToggleColumn, setHasToggleColumn] = useState(false);
    const [activeResizeColIndex, setActiveResizeColIndex] = useState<number>();
    const [currentPosition, setCurrentPosition] = useState<number>(0);
    const [currentWidth, setCurrentWidth] = useState<number>(0);

    useEffect(() => {
        if (pageSize)
            setPageSize(pageSize)
    }, [pageSize]);

    useEffect(() => {
        if (tableData) {
            for (let i = 0; i < tableData.length; i++) {
                tableData[i]._rowIndex = i;
            }
            dataProcessor.setData(tableData);
            dataProcessor.process();

            changePageNumber(currentPage);
        }

        // eslint-disable-next-line
    }, [tableData]);

    useEffect(() => {
        if (name && colInfos) {
            let userSettings: IColumnUserSettings[] = []
            let newUserSettings: IColumnUserSettings[] = []

            const userSettingsJson = localStorage.getItem(name + "-user-settings");
            if (userSettingsJson)
                userSettings = JSON.parse(userSettingsJson);

            colInfos.forEach((colInfo) => {
                let userSetting: IColumnUserSettings
                const existingUserSettings = userSettings.filter((item) => item.dataProp === (colInfo.dataProp ?? colInfo.title ?? ''))
                if (existingUserSettings.length >= 1) {
                    newUserSettings.push(existingUserSettings[0])
                }
                else {
                    userSetting = {
                        dataProp: colInfo.dataProp ?? colInfo.title ?? '',
                        visible: false,
                        sort: colInfo.sort,
                        sortPrio: colInfo.sortPrio,
                        width: colInfo.width,
                        selectedFilterValues: colInfo.filterInfo?.filterValues ?? [],
                    }

                    if (colInfo.visible !== false)
                        userSetting.visible = true

                    newUserSettings.push(userSetting)
                }
            })
            dataProcessor.setColInfos(colInfos, newUserSettings);
            dataProcessor.process();

            // save cols after setColInfo so the filteroptions are set in the colInfos.filterInfo
            setCols(x => colInfos)
            setColUserSettings((x) => newUserSettings)
            localStorage.setItem(name + "-user-settings", JSON.stringify(newUserSettings));

            changePageNumber(currentPage);
        }

        // eslint-disable-next-line
    }, [name, colInfos])

    function onHoverGetIndex(colIndex: number | undefined) {
        setActiveResizeColIndex(x => colIndex);

        if (colUserSettings) {
            colUserSettings.forEach((col, i) => {
                if (i === colIndex) {
                    const colInfo = cols[i]
                    if (colInfo.ref) {
                        const rect = colInfo.ref.current.getBoundingClientRect()
                        setCurrentPosition(x => rect.right)
                        setCurrentWidth(x => rect.width)
                    }
                }
            })
        }
    }

    const mouseMove = useCallback((e) => {
        e.preventDefault();
        if (colUserSettings && activeResizeColIndex !== undefined) {
            colUserSettings.forEach((col, i) => {
                const colInfo = cols[i]
                if (colInfo.ref.current) {
                    //const rect = colInfo.ref.current.getBoundingClientRect()

                    if (i !== activeResizeColIndex) {
                        //var width = rect.width
                        //colInfo.ref.current.style.width = `${width}px`;
                        //col.width = width
                    }
                    else {
                        const width = currentWidth + e.clientX - currentPosition;

                        let minCellWidth = colInfo.minWidth ? colInfo.minWidth : 0
                        if (width >= minCellWidth) {
                            colInfo.ref.current.style.width = `${width}px`;
                            col.width = width
                        }
                    }
                }
            });

            localStorage.setItem(name + "-user-settings", JSON.stringify(colUserSettings));
        }

        // eslint-disable-next-line
    }, [activeResizeColIndex, cols]);

    const removeListeners = useCallback(() => {
        window.removeEventListener('mousemove', mouseMove);
        window.removeEventListener('mouseup', removeListeners);
    }, [mouseMove]);

    const mouseUp = useCallback(() => {
        setActiveResizeColIndex(undefined);
        removeListeners();

        // eslint-disable-next-line
    }, [activeResizeColIndex, removeListeners]);

    useEffect(() => {
        if (activeResizeColIndex !== null) {
            window.addEventListener('mousemove', mouseMove);
            window.addEventListener('mouseup', mouseUp);
        }

        return () => {
            removeListeners();
        }
    }, [activeResizeColIndex, mouseMove, mouseUp, removeListeners]);

    function changeSort(colIndex: number) {
        for (let i = 0; i < colUserSettings.length; i++) {
            if (i !== colIndex && colUserSettings[i].sort !== false)
                colUserSettings[i].sort = true;
        };

        let colUser = colUserSettings[colIndex]
        if (colUser.sort === "asc") {
            colUser.sort = "desc";
        } else {
            colUser.sort = "asc"
        }

        setColUserSettings((x) => [...colUserSettings])
        localStorage.setItem(name + "-user-settings", JSON.stringify(colUserSettings));

        dataProcessor.setColInfos(cols, colUserSettings);
        dataProcessor.process();

        changePageNumber(1);
    }

    function changeFilter(colIndex: number, value: any[]) {
        colUserSettings[colIndex].selectedFilterValues = value
        setColUserSettings((x) => [...colUserSettings])
        localStorage.setItem(name + "-user-settings", JSON.stringify(colUserSettings));

        dataProcessor.setColInfos(cols, colUserSettings);
        dataProcessor.process();

        changePageNumber(1);
    }


    function hideShowFilter(colInfo: IColInfo, e, colInd: number) {
        let left = 2
        for (var i = 0; i < colInd; i++) {
            if (colInfos[i].ref?.current)
                left += colInfos[i].ref.current.clientWidth + 1
        }

        let tableNode = e.currentTarget.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode
        let scrollLeft = tableNode.scrollLeft
        let tableWidth = tableNode.clientWidth

        let menuWidth = 300
        if (colInfo.filterInfo?.filterMenuWidth)
            menuWidth = colInfo.filterInfo.filterMenuWidth

        if (left + menuWidth + 24 - scrollLeft > tableWidth)
            left = tableWidth - menuWidth - 24 + scrollLeft

        setFilterColInfo(colInfo)
        setFilterColIndex(colInd)
        setFilterMenuLeft(left - scrollLeft)
        setFilterMenuTop(e.currentTarget.parentNode.parentNode.clientHeight + 3)
        setShowFilterMenu(x => !x)
    }

    function hideFilter() {
        setShowFilterMenu(false)
    }

    function onNavigate(id) {
        if (navigateTitle && navigateUrl) {
            navigate(navigateUrl + id, { state: { title: navigateTitle, fromMenu: false } });
        }
    }

    function changePageNumber(pageNumber: number) {
        if (tableData) {
            if (pageNumber > Math.ceil(tableData.length / currentPageSize))
                pageNumber = Math.ceil(tableData.length / currentPageSize)
        }

        if (pageNumber < 1)
            pageNumber = 1

        let end = (currentPageSize * pageNumber)
        let start = end - currentPageSize

        setCurrentPage(pageNumber)
        setStartRow(start)
        setEndRow(end)

        let processedData = dataProcessor.getProcessedData(start, end)
        setData(processedData)
        setTotalFilteredRows(dataProcessor.getFilteredAndSortedData().length)
    }

    function changePageSize(newPageSize: number) {
        if (pageSize) {
            let pageNumber = Math.ceil(startRow / newPageSize)
            let end = (newPageSize * pageNumber) - 1
            let start = end - newPageSize + 1

            setPageSize(newPageSize);
            setCurrentPage(pageNumber)
            setStartRow(start);
            setEndRow(end)

            let processedData = dataProcessor.getProcessedData(start, end)
            setData(processedData)
        }
    }

    function resetFilter() {
        if (name) {
            cols.forEach((colInfo, index) => {
                let colUser = colUserSettings[index]
                colUser.selectedFilterValues = colInfo.filterInfo?.filterValues ?? []
            });

            dataProcessor.setColInfos(cols, colUserSettings);
            dataProcessor.process();

            changePageNumber(1);

            setColUserSettings(x => [...colUserSettings])
            localStorage.setItem(name + "-user-settings", JSON.stringify(colUserSettings));
        }
    }

    function resetCustomisations() {
        if (name) {
            cols.forEach((colInfo, index) => {
                let colUser = colUserSettings[index]
                colUser.sort = colInfo.sort
                colUser.sortPrio = colInfo.sortPrio
                colUser.width = colInfo.width
                colUser.selectedFilterValues = colInfo.filterInfo?.filterValues ?? []
            });

            dataProcessor.setColInfos(cols, colUserSettings);
            dataProcessor.process();

            changePageNumber(1);

            setColUserSettings(x => [...colUserSettings])
            localStorage.setItem(name + "-user-settings", JSON.stringify(colUserSettings));
        }
    }

    function exportData() {
        if (data) {
            const dataExporter = new DataExporter();
            dataExporter.downLoad(name, data, cols)
        }
    }

    function onCheckboxButtonClicked(colInfo: IColInfo) {
        if (data !== undefined && colInfo.checkboxButton !== undefined) {
            if (colInfo.checkboxButton.onClick) {
                // handle click if own method
                const checkedRows = data.filter(rowData => rowData['_checkbox_' + colInfo.checkboxButton?.text] === true);
                colInfo.checkboxButton.onClick(checkedRows);
            }
            else {
                // basic select all behaviour
                if (data.length !== data.filter(rowData => rowData['_checkbox_' + colInfo.checkboxButton?.text] === true).length)
                    data.forEach(rowData => rowData['_checkbox_' + colInfo.checkboxButton?.text] = true)
                else
                    data.forEach(rowData => rowData['_checkbox_' + colInfo.checkboxButton?.text] = false)
            }
            changePageNumber(currentPage);
            selectedRowsChange(colInfo);
        }
    }

    function selectedRowsChange(colInfo: IColInfo) {
        if (tableData && onSelectedRowsChange) {
            var selectedRows = tableData.filter(x => x['_checkbox_' + colInfo.checkboxButton?.text] === true)
            onSelectedRowsChange(selectedRows, colInfo);
        }
    }

    const setToggleColumn = () => {
        setHasToggleColumn(x => !x)
    }

    const onChangeToggleColumn = (value: string[]) => {
        if (colUserSettings) {
            colUserSettings.forEach((col, colIndex) => {
                if (value.filter(x => x === col.dataProp).length > 0)
                    col.visible = true;
                else {
                    const colInfo = cols[colIndex]
                    if ((colInfo.title || colInfo.dataProp) && colInfo.visible !== false)
                        col.visible = false;
                }
            })
            setColUserSettings(x => [...colUserSettings])
            localStorage.setItem(name + "-user-settings", JSON.stringify(colUserSettings));
        }
    }

    const rowClickable = ((navigateTitle !== undefined && navigateUrl !== undefined) || onRowClick !== undefined) ? true : false

    const classnameEditable = cols.filter(x => x.editable === true).length > 0 ? ' plm-datatable-editable' : ''
    const classnameScollable = maxHeight ? 'plm-datatable-scrollable' : '' 

    return (
        <>
            <div className={`plm-datatable${classnameEditable}${classnameScollable}`} style={{ maxWidth: maxWidth ? maxWidth + 40 : 'none' }}>
                {showToggleColumn &&
                    <div className={`plm-datatable-config ${(data && data.length > 0) ? ' withdata' : ''}`} >
                        <span className={'filter-icon'} onClick={setToggleColumn}>
                            <i className={'fa fa-cogs'} />
                        </span>
                        {data && data.length > 0 && <span style={{ color: 'green' }} className={'filter-icon'} onClick={exportData}>
                            <i className={'fa fa-file-excel-o'} />
                        </span>}
                        <span className={'filter-icon'} onClick={resetFilter}>
                            <i className={'fa fa-filter'} />
                        </span>
                        <span className={'filter-icon'} onClick={resetCustomisations}>
                            <i className={'fa fa-undo'} />
                        </span>
                        {hasToggleColumn && colUserSettings && <ToggleColumn colInfos={colInfos} colUserSettings={colUserSettings} onChangeToggleColumn={onChangeToggleColumn} />}
                    </div>
                }
                <div className="plm-datatable-wrapper" style={{ maxHeight: maxHeight ? maxHeight : 'none', maxWidth: maxWidth ? maxWidth : 'none' }}>
                    <table id={name} className={`table table-bordered table-striped table-hover table-sm plm-datatable-table ${maxHeight ? 'plm-datatable-scrollable-table' : ''} ${rowClickable === true ? 'table-row-clickable' : ''}`}>
                        <TableHeader tableName={name} colInfos={cols} colUserSettings={colUserSettings} colResizeActiveIndex={activeResizeColIndex} onHoverGetIndex={onHoverGetIndex} onChangeSort={changeSort} onChangeFilter={changeFilter} hideShowFilter={hideShowFilter} key={"table-Header"} onCheckboxButtonClicked={onCheckboxButtonClicked} resizable={resizable} />
                        <TableBody colInfos={cols} colUserSettings={colUserSettings} tableData={tableData ? data : tableData} refreshNr={refreshNr} onNavigate={onNavigate} onRowClick={onRowClick} serverErrors={serverErrors} key={refreshNr} onCellChange={onCellChange} onSelectedRowsChange={selectedRowsChange} />
                    </table>
                </div>
                {data && tableData && pageSize &&
                    <div className="bos-datatable-wrapper" style={{ maxWidth: maxWidth ? maxWidth : 'none' }}>
                        <table id={name} className={`table table-bordered table-striped table-hover table-sm bos-datatable-table `}>
                            <TableFooter colInfos={cols} colUserSettings={colUserSettings} startRow={startRow} endRow={endRow} totalRows={tableData.length} totalFilteredRows={totalFilteredRows} totalPages={Math.ceil(tableData.length / currentPageSize)} totalFilteredPages={Math.ceil(totalFilteredRows / currentPageSize)} pageSize={currentPageSize} currentPage={currentPage} changePageNumber={changePageNumber} changePageSize={changePageSize} key='footer' />
                        </table>
                    </div>
                }
                {showFilterMenu && <Filter colInfo={filterColInfo} colUserSettings={colUserSettings[filterColIndex]} colIndex={filterColIndex} menuWidth={filterColInfo.filterInfo?.filterMenuWidth ?? 300} menuHight={filterColInfo.filterInfo?.filterMenuHeight ?? 0} menuTop={filterMenuTop} menuLeft={filterMenuLeft} onChangeFilter={changeFilter} hideFilter={hideFilter} />}
            </div >
        </>
    );
}
export default DataTable;
